import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bot, X, Compass } from "lucide-react";
import DepthTip from "@/components/DepthTip";

// Patch 62 Part 3 — the Socratic AI Co-Pilot. A visually bold, brand-carrying floating overlay that
// is the SAME real BeCo Coach engine (Patch 40: before/during/after voice, sensitivity dial,
// disagreement memory, guiding-question style) given a new, prominent presentation — not a second
// assistant, not a new brand beyond this name, no new capability, no emotion inference, no persona.
// It always surfaces the real ten-metric, patent-pending BeCo Index (Patch 39) verbatim from the
// backend, and it NEVER blocks: it defers to the Perimeter Gate and the margin-guard stop-loss.
const COPILOT_OPEN_KEY = "kadana_copilot_open"; // Patch 68 — persist open/dismissed choice per browser (user)

export default function SocraticCopilot({ projectId, prompt, deferToGate }) {
  const [beco, setBeco] = useState(null);
  const [tips, setTips] = useState(null);
  // Starts CLOSED by default. A stored "1" (user summoned) keeps it open across refreshes/page
  // changes; a stored "0" (user dismissed) keeps it dismissed. Absent → closed.
  const [open, setOpen] = useState(() => { try { return localStorage.getItem(COPILOT_OPEN_KEY) === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(COPILOT_OPEN_KEY, open ? "1" : "0"); } catch { /* ignore */ } }, [open]);

  useEffect(() => { api.get("/coach/beco/me").then((r) => setBeco(r.data)).catch(() => {}); }, [projectId]);

  // Reuse the Coach's OWN existing trigger surface (/coach/prompt-tips) — no new trigger logic.
  useEffect(() => {
    const p = (prompt || "").trim();
    if (p.length < 4) { setTips(null); return; }
    const t = setTimeout(() => {
      api.post("/coach/prompt-tips", { prompt: p, session_id: `proj-${projectId}` })
        .then((r) => setTips(r.data)).catch(() => {});
    }, 1400);
    return () => clearTimeout(t);
  }, [prompt, projectId]);

  useEffect(() => {
    const onSummon = () => setOpen(true);
    window.addEventListener("summon-copilot", onSummon);
    return () => window.removeEventListener("summon-copilot", onSummon);
  }, []);

  const coachFired = !!(tips && (tips.note || (tips.missing_feature_alerts || []).length || (tips.enhancement_chips || []).length));

  // Genuinely-blocking gates take priority; the overlay defers exactly as the Coach's scoped rule requires.
  if (deferToGate) return null;
  if (!open || !coachFired) return null;

  const guiding = (tips.missing_feature_alerts || [])[0]
    || (tips.enhancement_chips || [])[0]?.label
    || "What outcome should a first-time user reach on their very first visit?";

  return (
    <div data-testid="socratic-copilot" role="complementary" aria-label="Socratic AI Co-Pilot"
      style={{ position: "static", width: "100%", marginTop: 12,
        borderRadius: 16, padding: 14, background: "var(--container)", border: "1px solid var(--accent-primary)",
        boxShadow: "0 0 0 3px rgba(154,222,224,.12)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ width: 30, height: 30, borderRadius: 10, display: "grid", placeItems: "center", background: "var(--accent-primary)", color: "#111113" }}><Bot size={17} /></span>
        <DepthTip testid="copilot-icon-tip"
          summary="Socratic AI Co-Pilot — your BeCo Coach, asking guiding questions."
          expanded="The same BeCo Coach engine (before/during/after voice, sensitivity dial, disagreement memory). It asks guiding questions rather than giving answers; it never blocks a build and defers to the Perimeter Gate and the margin-guard stop-loss.">
          <strong className="k-heading" style={{ fontSize: 14 }}>Socratic AI Co-Pilot</strong>
        </DepthTip>
        <button className="k-btn k-btn-ghost" data-testid="copilot-dismiss" aria-label="Dismiss Co-Pilot"
          style={{ marginLeft: "auto", padding: "3px 7px", minHeight: 0 }} onClick={() => setOpen(false)}><X size={14} /></button>
      </div>

      {/* The real BeCo Index — value, methodology, subjective label and patent footnote rendered verbatim from the backend. */}
      {beco && (
        <div data-testid="copilot-beco" style={{ borderRadius: 12, padding: "10px 12px", marginBottom: 10, background: "var(--canvas)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Compass size={14} aria-hidden="true" />
            <DepthTip testid="copilot-beco-tip" summary={beco.methodology} expanded={beco.subjective_note}>
              <span className="k-heading" style={{ fontSize: 13 }}>BeCo Index</span>
            </DepthTip>
            <span className="k-heading" data-testid="copilot-beco-value" style={{ marginLeft: "auto", fontSize: 26, fontWeight: 700 }}>{beco.index}</span>
            <span className="k-muted" style={{ fontSize: 11 }}>/100</span>
          </div>
          <div style={{ display: "flex", gap: 2, marginTop: 6 }} aria-hidden="true">
            {Array.from({ length: 20 }).map((_, i) => (
              <span key={i} style={{ flex: 1, height: 6, borderRadius: 2, background: i < Math.round(beco.index / 5) ? "var(--accent-primary)" : "var(--control)" }} />
            ))}
          </div>
          <p className="k-muted" style={{ fontSize: 10.5, marginTop: 8, lineHeight: 1.5 }}>{beco.methodology}</p>
          <p className="k-muted" data-testid="copilot-beco-footnote" style={{ fontSize: 10, marginTop: 6, opacity: 0.85 }}>*{beco.patent_footnote}</p>
        </div>
      )}

      {/* The Coach's guiding (Socratic) question — same engine as the inline Coach voice. */}
      <div data-testid="copilot-message">
        <div className="k-muted" style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>A question to consider</div>
        <p className="k-heading" style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>{guiding}</p>
        {tips?.note && <p className="k-muted" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>{tips.note}</p>}
      </div>
    </div>
  );
}
